import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';

import { PartDemand } from '../../services/partDemandTypes';
import { formatDate, percent } from '../quoteToCash/format';

import { customerLabel, formatAverage, formatQty } from './format';
import { MiniColumns, MonthStrip, ShareBar, Stat, StatBand } from './Stats';

/** The part and its headline demand figures, in one card */
export const PartDemandSummary: React.FC<{ data: PartDemand }> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { part, history, kpis } = data;
  const qty = (value: number) => formatQty(value, part.uom, lang);

  const header: [string, string][] = [
    [t('partDemand.header.class'), part.partClass],
    [t('partDemand.header.type'), part.partType],
    [t('partDemand.header.uom'), part.uom],
  ];

  const complete = data.monthly.filter((m) => !m.partial);
  const period = complete.slice(-kpis.analysisMonths);
  const monthLabel = (month: string) =>
    formatDate(`${month}-01`, lang, 'monthYear');
  const trailing = ([3, 6, 12] as const)
    .map((months) => ({
      months,
      value: { 3: kpis.demand3M, 6: kpis.demand6M, 12: kpis.demand12M }[months],
    }))
    .filter((p) => p.value !== null)
    .map((p) =>
      t('partDemand.kpi.lastMonthsValue', {
        count: p.months,
        qty: qty(p.value as number),
      })
    );
  // The largest customer, the next two, and everyone else, as shares of customer demand
  const shares = data.customers.map((c) => c.pctOfCustomerDemand);
  const top = data.customers[0];
  const shareParts = [
    { label: top ? customerLabel(top) : '', share: shares[0] ?? 0 },
    {
      label: t('partDemand.kpi.next2'),
      share: (shares[1] ?? 0) + (shares[2] ?? 0),
    },
    {
      label: t('partDemand.kpi.others'),
      share: shares.slice(3).reduce((total, share) => total + share, 0),
    },
  ];

  return (
    <div className="rounded-lg border bg-white shadow-sm p-4 sm:px-6 mb-6">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-2xl font-semibold text-gray-900">
          {part.partNumUM || part.partNum}
        </h2>
        {part.partNumUM && (
          <span className="text-sm text-gray-500">{part.partNum}</span>
        )}
        <span className="text-gray-700">{part.description}</span>
        {part.inactive && (
          <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-700">
            {t('partDemand.header.inactive')}
          </span>
        )}
      </div>
      <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-x-4 gap-y-2 text-sm">
        {header.map(([label, value]) => (
          <div key={label}>
            <dt className="text-gray-500">{label}</dt>
            <dd className="text-gray-900">{value || '—'}</dd>
          </div>
        ))}
      </dl>
      {history.availableMonths < 12 && (
        <p className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-sm text-amber-900">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          {t('partDemand.limitedHistory', {
            count: history.availableMonths,
            date: formatDate(history.startDate, lang, 'long'),
          })}
        </p>
      )}

      <div className="mt-4 pt-4 border-t border-gray-200">
        <StatBand boxed={false}>
          <Stat
            label={t('partDemand.kpi.demandOver', {
              count: kpis.analysisMonths,
            })}
            value={qty(kpis.demandAnalysis)}
            suffix={part.uom}
            detail={
              <>
                {trailing.length > 0 && <p>{trailing.join(' · ')}</p>}
                <p>
                  {t('partDemand.kpi.splitLine', {
                    customers: qty(kpis.customerDemand),
                    internal: qty(kpis.internalConsumption),
                    returns: qty(kpis.returns),
                  })}
                </p>
              </>
            }
          >
            <MiniColumns
              format={qty}
              values={data.monthly.map((m) => ({
                label: monthLabel(m.month),
                value: m.totalDemand,
                partial: m.partial,
              }))}
            />
          </Stat>
          <Stat
            label={t('partDemand.kpi.events')}
            value={String(kpis.demandEvents)}
            suffix={t('partDemand.kpi.inMonths', {
              active: kpis.activeMonths,
              months: kpis.analysisMonths,
            })}
            title={t('partDemand.kpi.eventsHelp')}
            detail={
              kpis.lastDemandDate === null
                ? t('partDemand.kpi.noDemand')
                : [
                    kpis.averagePerEvent === null
                      ? ''
                      : t('partDemand.kpi.perEvent', {
                          qty: formatAverage(kpis.averagePerEvent, lang),
                        }),
                    t('partDemand.kpi.lastDemand', {
                      date: formatDate(kpis.lastDemandDate, lang, 'long'),
                    }),
                  ]
                    .filter(Boolean)
                    .join(' · ')
            }
          >
            <MonthStrip
              months={period.map((m) => ({
                label: `${monthLabel(m.month)}: ${qty(m.totalDemand)}`,
                active: m.totalDemand > 0,
              }))}
            />
          </Stat>
          <Stat
            label={t('partDemand.kpi.customers')}
            value={String(kpis.distinctCustomers)}
            detail={
              kpis.topCustomerPct === null || kpis.top3CustomersPct === null
                ? t('partDemand.kpi.noCustomerDemand')
                : t('partDemand.kpi.topLine', {
                    name: top ? customerLabel(top) : '',
                    top: percent(kpis.topCustomerPct, lang, 0),
                    top3: percent(kpis.top3CustomersPct, lang, 0),
                  })
            }
          >
            {kpis.topCustomerPct !== null && <ShareBar shares={shareParts} />}
          </Stat>
        </StatBand>
      </div>
    </div>
  );
};
